/**
 * Вопросы о несохранённых правках в диалоге редактора (для
 * useUnsavedChangesGuard): закрытие вкладки и кнопка «Назад» браузера.
 *
 * Диалоги кабинета открываются параметрами адреса (`?tab=edit&id=`), и
 * «Назад» закрывает их без вопроса: роутер получает popstate и снимает
 * параметры. Кабинет живёт на обычном BrowserRouter, а `useBlocker`
 * react-router 7 работает только с data router (createBrowserRouter),
 * поэтому переход перехватываем сами тем же способом, что и сама
 * библиотека: один слушатель popstate ставится при старте приложения
 * раньше роутера (installBackNavigationListener), а диалоги с правками
 * регистрируются в нём. Если партнёр остаётся, событие до роутера не
 * доходит, а браузер возвращается на прежнюю запись (`history.go` на
 * разницу индексов `idx`, которые роутер пишет в `history.state`).
 * Возврат тоже шлёт popstate, его гасим так же.
 */

/** Параметры адреса, которыми открыт диалог. */
export const DIALOG_PARAMS: readonly string[] = ["tab", "id"];

/** Нужная часть window: подменяется в тестах. */
export interface GuardWindow {
  location: { pathname: string; search: string };
  history: { state: unknown; go: (delta: number) => void };
  confirm: (message: string) => boolean;
  addEventListener: (
    type: "popstate" | "beforeunload",
    listener: (event: Event) => void,
    capture?: boolean
  ) => void;
  removeEventListener: (
    type: "popstate" | "beforeunload",
    listener: (event: Event) => void,
    capture?: boolean
  ) => void;
}

interface DialogEntry {
  pathname: string;
  params: string;
  idx: number | null;
}

/** Индекс записи истории, который роутер хранит в `history.state.idx`. */
export function historyIndex(state: unknown): number | null {
  if (typeof state !== "object" || state === null) return null;
  const { idx } = state as { idx?: unknown };
  return typeof idx === "number" ? idx : null;
}

const dialogParams = (search: string, params: readonly string[]): string => {
  const query = new URLSearchParams(search);
  return JSON.stringify(params.map((name) => query.get(name)));
};

/** Адрес диалога глазами роутера: путь и строка запроса. */
export interface DialogLocation {
  pathname: string;
  search: string;
}

const entryOf = (
  win: GuardWindow,
  params: readonly string[],
  location: DialogLocation = win.location
): DialogEntry => ({
  pathname: location.pathname,
  params: dialogParams(location.search, params),
  idx: historyIndex(win.history.state),
});

interface ActiveGuard {
  message: string;
  params: readonly string[];
  entry: DialogEntry;
}

interface BackListenerState {
  guards: ActiveGuard[];
  restoring: boolean;
}

/**
 * Один слушатель popstate на окно и реестр диалогов с несохранёнными
 * правками. Слушатель ставится при старте приложения, до роутера
 * (installBackNavigationListener в точке входа): для событий на самом
 * window браузер вызывает слушатели в порядке регистрации, флаг захвата
 * очерёдность не меняет (Chrome, Safari). Роутер, зарегистрированный
 * раньше, успел бы синхронно закрыть диалог и снять перехват до его
 * вызова; так и было, пока перехват ставился из эффекта диалога.
 */
const listeners = new WeakMap<GuardWindow, BackListenerState>();

const handlePopState =
  (win: GuardWindow, state: BackListenerState) => (event: Event) => {
    if (state.restoring) {
      // Это наш возврат на запись диалога: роутер его видеть не должен,
      // для него адрес и не менялся.
      state.restoring = false;
      event.stopImmediatePropagation();
      return;
    }
    const guard = state.guards[state.guards.length - 1];
    if (!guard) return;
    const next = entryOf(win, guard.params);
    // Переход внутри того же диалога (те же путь, tab и id) его не
    // закрывает, правки не теряются.
    if (
      next.pathname === guard.entry.pathname &&
      next.params === guard.entry.params
    ) {
      guard.entry = next;
      return;
    }
    if (win.confirm(guard.message)) return;
    event.stopImmediatePropagation();
    // Без индексов (запись не от роутера) считаем, что это был один шаг
    // назад: самый частый случай. Равные индексы при ушедшем адресе значат,
    // что снимок сделан уже после перехода: это тоже один шаг назад.
    const delta =
      guard.entry.idx !== null &&
      next.idx !== null &&
      guard.entry.idx !== next.idx
        ? guard.entry.idx - next.idx
        : 1;
    state.restoring = true;
    win.history.go(delta);
  };

/**
 * Ставит общий слушатель «Назад» на окно. Вызывается один раз при старте
 * приложения, раньше роутера; повторный вызов ничего не делает.
 */
export function installBackNavigationListener(win: GuardWindow): void {
  if (listeners.has(win)) return;
  const state: BackListenerState = { guards: [], restoring: false };
  listeners.set(win, state);
  win.addEventListener("popstate", handlePopState(win, state));
}

/**
 * Регистрирует диалог с несохранёнными правками и возвращает функцию
 * снятия. Регистрируется только пока есть правки: без них «Назад» уходит
 * сразу, как раньше. Если общий слушатель ещё не поставлен (тесты, другое
 * приложение), ставит его сейчас: тогда очерёдность с роутером не
 * гарантирована.
 */
export function installBackNavigationGuard(
  win: GuardWindow,
  message: string,
  params: readonly string[] = DIALOG_PARAMS,
  dialogLocation?: DialogLocation
): () => void {
  installBackNavigationListener(win);
  const state = listeners.get(win);
  if (!state) return () => undefined;
  // Снимок адреса диалога берётся у роутера, а не у окна: между вызовом
  // «Назад» и событием popstate адрес окна уже следующий, а эффект хука за
  // это время успевает перерегистрировать диалог.
  const guard: ActiveGuard = {
    message,
    params,
    entry: entryOf(win, params, dialogLocation),
  };
  state.guards.push(guard);
  return () => {
    const at = state.guards.indexOf(guard);
    if (at >= 0) state.guards.splice(at, 1);
  };
}

/**
 * Вопрос при закрытии вкладки и перезагрузке: стандартный `beforeunload`.
 * Перезагрузку, которую запускает само приложение после сохранения
 * (reloadWithoutPrompt), не спрашиваем.
 */
export function installUnloadPrompt(
  win: GuardWindow,
  message: string,
  isSuppressed: () => boolean
): () => void {
  const onBeforeUnload = (event: Event) => {
    if (isSuppressed()) return undefined;
    event.preventDefault();
    // Старые браузеры показывают вопрос только с непустым returnValue.
    (event as BeforeUnloadEvent).returnValue = message;
    return message;
  };
  win.addEventListener("beforeunload", onBeforeUnload);
  return () => win.removeEventListener("beforeunload", onBeforeUnload);
}

/**
 * Тело эффекта useUnsavedChangesGuard: без правок ничего не ставит (уход
 * сразу, в том числе после успешного сохранения), с правками ставит оба
 * вопроса и возвращает их снятие.
 */
export function guardUnsavedChanges(
  win: GuardWindow,
  options: {
    dirty: boolean;
    message: string;
    params: readonly string[];
    isUnloadSuppressed: () => boolean;
    dialogLocation?: DialogLocation;
  }
): (() => void) | undefined {
  if (!options.dirty) return undefined;
  const removeUnloadPrompt = installUnloadPrompt(
    win,
    options.message,
    options.isUnloadSuppressed
  );
  const removeBackGuard = installBackNavigationGuard(
    win,
    options.message,
    options.params,
    options.dialogLocation
  );
  return () => {
    removeUnloadPrompt();
    removeBackGuard();
  };
}
